import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { EnvelopeSimple, PaperPlaneTilt, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Contact() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 10) {
      toast.error("Please add a little more detail (at least 10 characters).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", { name, email, message, website });
      setSent(true);
      setMessage("");
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50" data-testid="contact-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-center">
          <div className="font-mono-data text-mute inline-flex items-center gap-2 justify-center">
            <EnvelopeSimple size={14} weight="duotone" /> get in touch
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-ink leading-tight mt-2">
            We&apos;d love to hear from you.
          </h1>
          <p className="text-mute mt-4 leading-relaxed max-w-xl mx-auto">
            Questions, feedback, partnerships, or a plant story worth sharing — send a note and we&apos;ll get back within one business day.
          </p>
        </div>

        {sent ? (
          <div
            className="mt-12 bg-sand-100 border border-sand-200 rounded-3xl p-10 text-center"
            data-testid="contact-success"
          >
            <CheckCircle size={56} weight="duotone" className="text-forest mx-auto" />
            <h2 className="font-display text-3xl text-ink mt-4">Message received</h2>
            <p className="text-mute mt-2 max-w-md mx-auto">
              Thanks{name ? `, ${name}` : ""} — we&apos;ll get back to you at <span className="font-mono-data text-ink">{email}</span> soon.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => setSent(false)}
                data-testid="contact-send-another"
                className="btn-secondary"
              >
                Send another
              </button>
              <Link to="/" className="btn-primary">Back to home</Link>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-10 bg-sand-100 border border-sand-200 rounded-3xl p-6 md:p-8 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="c-name" className="font-mono-data text-mute text-xs">Your name</label>
                <input
                  id="c-name"
                  data-testid="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={100}
                  placeholder="Ada Bloom"
                  className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                />
              </div>
              <div>
                <label htmlFor="c-email" className="font-mono-data text-mute text-xs">Your email</label>
                <input
                  id="c-email"
                  data-testid="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest"
                />
              </div>
            </div>

            <div>
              <label htmlFor="c-message" className="font-mono-data text-mute text-xs">Message</label>
              <textarea
                id="c-message"
                data-testid="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                minLength={10}
                maxLength={4000}
                rows={7}
                placeholder="How can we help?"
                className="mt-1 w-full bg-white border border-sand-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-forest resize-y"
              />
              <div className="text-xs text-mute font-mono-data mt-1">
                {message.length}/4000
              </div>
            </div>

            {/* Honeypot — hidden from real users, tempting to bots. */}
            <div className="hidden" aria-hidden>
              <label htmlFor="c-website">Website (leave blank)</label>
              <input
                id="c-website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between gap-3 pt-2">
              <div className="text-xs text-mute flex items-center gap-2">
                <Warning size={12} weight="duotone" />
                We&apos;ll only reply to your question. No newsletters, no data sold.
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="contact-submit"
                className="btn-primary"
              >
                <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
